import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ElementUszkodzenia} from '../../../../../../../../../../services/slownik/types/element-uszkodzenia';
import {UuidGeneratorService} from '../../../../../../../../../../services/id-generator/uuid-generator.service';
import {Uszkodzenie} from '../../../../../../../../../../store/zbior-uszkodzen/zbiorUszkodzen';
import {isNumeric} from '../../../../../../../../../../validators/ControlValidators';
import {roundDecimal} from '../../../../../../../../../../util/math-functions';
import {FormularzUszkodzenService} from '../../formularz-uszkodzen.service';

interface ParametryFormularzaGrupowego {
  szerokosc: string,
  wysokosc: string
  materialWykonczenia: string;
  wartosc: string;
  foliaReklamowa: boolean;
}

interface WartoscFormularza<N> {
  id: string,
  value: N
}

@Component({
  selector: 'shared-grupa-uszkodzen',
  templateUrl: './grupa-uszkodzen.component.html'
})

export class GrupaUszkodzenComponent implements OnInit {
  constructor(private readonly idGenerator: UuidGeneratorService, private formularzUszkodzenService: FormularzUszkodzenService) {
  }

  @Input()
  formularzUszkodzenia!: UntypedFormGroup;

  @Input()
  elementUszkodzenia!: ElementUszkodzenia;

  @Input()
  formularzWidoczny!: boolean;

  @Input()
  czyUszkodzenieDomyslnieWybrane = false;

  @Input()
  czyWalidacjaWgPowierzchni = false;

  @Input()
  minimalnyWymiar?: number

  @Input()
  maksymalnyWymiar?: number

  readonly maksymalnaWartosc = 50;
  readonly minimalnaWartosc = 0.1;

  @Output()
  wyczyscFormularz = new EventEmitter();

  formularze: { id: string, formularz: UntypedFormGroup; }[] = [];
  maksymalneWartosci: {id: string, maksymalnaWartoscUszkodzenia: number}[] = []

  private groupFormGroup!: UntypedFormGroup;

  private dodajFormularzUszkodzenia(): void {
    if (this.elementUszkodzenia.maksymalnaIloscElementow && this.formularze.length >= this.elementUszkodzenia.maksymalnaIloscElementow) {
      return;
    }
    this.inicjujFormularz();
    this.formularzUszkodzenService.sprawdzWalidacjeWDodanychFormularzach()
  }

  ngOnInit(): void {
    this.groupFormGroup = new UntypedFormGroup({});
    this.formularzUszkodzenia.addControl(this.elementUszkodzenia.nazwa + '_group', this.groupFormGroup)
    this.inicjujFormularz();
  }

  private inicjujFormularz(id?: string, parametry?: ParametryFormularzaGrupowego): void {
    const formularz = new UntypedFormGroup({});
    const min = this.elementUszkodzenia.minimalnaWartoscUszkodzenia;
    const max = this.elementUszkodzenia.maksymalnaWartoscUszkodzenia;

    if (!id) {
      id = this.idGenerator.generateUuId();
    }

    if (this.elementUszkodzenia.atrybuty.pokazSzerokosc) {
      formularz.addControl(
        'szerokosc',
        new FormControl<string | null>(
          parametry ? parametry.szerokosc : null,
          [
            Validators.required,
            Validators.min(min || this.minimalnaWartosc),
            Validators.max( max || this.maksymalnaWartosc),
            isNumeric()
          ]
        )
      )
    }
    if (this.elementUszkodzenia.atrybuty.pokazWysokosc) {
      formularz.addControl(
        'wysokosc',
        new FormControl<string | null>(
          parametry ? parametry.wysokosc : null,
          [
            Validators.required,
            Validators.min(min || this.minimalnaWartosc),
            Validators.max(max || this.maksymalnaWartosc),
            isNumeric()
          ]
        )
      )
    }

    if (this.elementUszkodzenia.atrybuty.pokazMaterialyWykonczenia) {
      formularz.addControl(
        'materialWykonczenia',
        new FormControl<string | null>(
          parametry ? parametry.materialWykonczenia : null,
          [Validators.required]
        )
      )
    }

    if (this.elementUszkodzenia.atrybuty.pokazWartosc) {
      this.maksymalneWartosci.push({id, maksymalnaWartoscUszkodzenia: 0})
      formularz.addControl(
        'wartosc',
        new FormControl<string | null>(
          parametry ? parametry.wartosc : null,
          [
            Validators.required,
            Validators.min(min || this.minimalnaWartosc),
            isNumeric()
          ]
        )
      )
    }

    if (this.elementUszkodzenia.atrybuty.pokazFolieReklamowa) {
      formularz.addControl(
        'foliaReklamowa',
        new FormControl<boolean>(parametry ? parametry.foliaReklamowa : false)
      )
    }

    this.formularze.push({id, formularz});
    this.groupFormGroup.addControl(id, formularz)
    this.formularzUszkodzenService.ostatnioDodanyFormularz = formularz
  }

  inicjujIstniejaceUszkodzenie(listaUszkodzenIstniejacych: Uszkodzenie<any>[]): void {
    this.formularze.forEach(f => {
      delete this.groupFormGroup.controls[f.id];
    })
    this.formularze = []
    this.maksymalneWartosci = []

    listaUszkodzenIstniejacych.forEach(uszkodzenie => {
      this.inicjujFormularz(uszkodzenie.identyfikator, uszkodzenie.parametry)
    })
    this.groupFormGroup.updateValueAndValidity()
  }

  private aktualizujFormularz($event: { id: string, formGroup: UntypedFormGroup }): void {
    if ($event && $event.id) {
      this.groupFormGroup.controls[$event.id] = $event.formGroup
      const form = this.formularze.find(f => f.id === $event.id)
      if (form) {
        form.formularz = $event.formGroup
        if (this.elementUszkodzenia.atrybuty.pokazWartosc) {
          this.ustawMaksymalnaPowierzchniaUszkodzenia(form)
        }
      }
    }
  }

  private wyczyscPodformularz($event: string): void {
    if ($event) {
      this.resetujFormularz($event)
    }
  }

  private resetujFormularz(id: string): void {
    const form = this.formularze.find(f => f.id === id);
    if (form) {
      const index = this.formularze.indexOf(form);
      delete this.groupFormGroup.controls[id];

      !this.czyUszkodzenieDomyslnieWybrane ?
        this.usunieciePodformularzaPrzyBrakuDomyslnieWybranego(index) :
        this.usunieciePodformularzaPrzyDomyslnieWybranym(index);

      this.groupFormGroup.updateValueAndValidity();
    }
  }

  private usunieciePodformularzaPrzyDomyslnieWybranym(index: number): void {
    if (this.formularze.length === 1) {
      this.usun(index);
      this.inicjujFormularz()
    } else {
      this.usun(index);
    }
  }

  private usun(index: number): void {
    this.formularze.splice(index, 1);
    this.maksymalneWartosci.splice(index, 1)
  }

  private usunieciePodformularzaPrzyBrakuDomyslnieWybranego(index: number): void {
    this.formularze.splice(index, 1);
    if (this.formularze.length === 0) {
      this.inicjujFormularz()
      this.wyczyscFormularz.emit()
    }
  }

  private ustawMaksymalnaPowierzchniaUszkodzenia(form: {id: string, formularz: UntypedFormGroup}): UntypedFormGroup {
    const maksymalnaPowierzchniaUszkodzenia = (form.formularz.value.szerokosc && form.formularz.value.wysokosc) ?
      roundDecimal(form.formularz.value.szerokosc * form.formularz.value.wysokosc) : 0;
    const maksymalnaWartosc = this.maksymalneWartosci.find(e => e.id === form.id)

    if (maksymalnaWartosc && maksymalnaWartosc.maksymalnaWartoscUszkodzenia !== maksymalnaPowierzchniaUszkodzenia) {
      maksymalnaWartosc.maksymalnaWartoscUszkodzenia = maksymalnaPowierzchniaUszkodzenia;
      form.formularz.controls.wartosc.setValidators([
        Validators.required,
        Validators.min(this.elementUszkodzenia.minimalnaWartoscUszkodzenia || this.minimalnaWartosc),
        Validators.max(maksymalnaPowierzchniaUszkodzenia), isNumeric()
      ]);

      setTimeout(() => {
        form.formularz.controls.wartosc.updateValueAndValidity({onlySelf: false, emitEvent: true})
      });
    }
    return form.formularz;
  }

  getUszkodzenia<T>(): WartoscFormularza<T>[] {
    return this.formularze.map(f => ({id: f.id, value: f.formularz.value}))
  }
}
